import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  MapPin,
  Calendar,
  ArrowRight,
  Building2,
  Ruler,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { useCMS } from "@/context/CMSContext";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function PageHeader() {
  return (
    <section className="pt-28 pb-12 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(219,106,64,0.25) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-left"
        >
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 text-sm mb-4"
          >
            <Link to="/" className="text-white/60 hover:text-accent transition-colors">
              Home
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-accent font-medium">Projects</span>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-xs mb-4"
          >
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            Our Portfolio
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-white mb-3"
          >
            Featured Projects
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-16 h-0.5 bg-accent origin-left mb-4 rounded-full"
          />

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-white/70 max-w-xl text-base"
          >
            Explore our completed surveying projects across Nigeria.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

export default function Projects() {
  const { projects, isLoading } = useCMS();

  const visibleProjects = projects.filter((project) => project.status !== "Draft");

  return (
    <main className="min-h-screen">
      <PageHeader />

      <section className="py-16 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
              <Building2 className="w-4 h-4" />
              Our Work
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              Recent Projects
            </h2>
          </AnimatedSection>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleProjects.map((project, index) => (
                <AnimatedSection key={project.id} delay={index * 0.1}>
                  <Link to={`/projects/${project.id}`} className="group block">
                    <div className="bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl hover:shadow-accent/5 hover:border-accent/20 transition-all duration-300">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={project.image_url}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />

                        <div className="absolute top-4 left-4">
                          <span className="bg-white/95 backdrop-blur-sm text-primary text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                            {project.category}
                          </span>
                        </div>

                        <div className="absolute top-4 right-4">
                          <span className="bg-green-500 text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                            <CheckCircle2 className="w-3 h-3" />
                            {project.status}
                          </span>
                        </div>
                      </div>

                      <div className="p-5">
                        <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-accent transition-colors line-clamp-1">
                          {project.title}
                        </h3>

                        <p
                          className="rich-text-content rich-text-content--preview text-neutral-500 text-sm line-clamp-2 mb-4 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: project.description }}
                        />

                        <div className="flex items-center gap-3 text-xs text-neutral-400 mb-4">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {project.location}
                          </div>
                          <span className="text-neutral-300">|</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {project.year}
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                          <span className="text-xs text-neutral-400 font-medium">
                            {project.client}
                          </span>
                          <span className="text-accent font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                            View
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          )}

          {!isLoading && visibleProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-neutral-400">No projects found.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-neutral-50">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
              <CheckCircle2 className="w-4 h-4" />
              By The Numbers
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              Our Track Record
            </h2>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { value: "300+", label: "Projects Completed", icon: Building2 },
              { value: "98%", label: "Client Satisfaction", icon: CheckCircle2 },
              { value: "36", label: "States Covered", icon: MapPin },
              { value: "08+", label: "Years Experience", icon: Ruler },
            ].map((stat, index) => (
              <AnimatedSection key={stat.label} delay={index * 0.1}>
                <div className="bg-white rounded-2xl p-6 text-center border border-neutral-100 hover:border-accent/20 hover:shadow-lg hover:shadow-accent/5 transition-all duration-300">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-neutral-500 text-sm">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
